import { useState, useRef } from 'react';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('name',    (form.elements.namedItem('name')    as HTMLInputElement).value);
    data.append('email',   (form.elements.namedItem('email')   as HTMLInputElement).value);
    data.append('subject', (form.elements.namedItem('subject') as HTMLInputElement).value);
    data.append('message', (form.elements.namedItem('message') as HTMLTextAreaElement).value);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6t3lea4of3kuoicmubg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputBase =
    'w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/60 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.08)] transition-all duration-200';

  return (
    <section className="py-14 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/3 w-80 h-80 bg-cyan-600/6 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-600/6 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-2xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 border border-cyan-500/25 rounded-full text-xs uppercase tracking-widest text-cyan-400 bg-cyan-500/5 mb-4">
            <i className="ri-mail-send-line text-cyan-400"></i>
            Get in Touch
          </div>
          <h2 className="text-4xl font-bold text-white mb-3">
            Contact Us
            <div className="h-1 w-24 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-4 rounded-full"></div>
          </h2>
          <p className="text-gray-400 text-base leading-relaxed">
            Have questions, suggestions, or business inquiries? We&apos;d love to hear from you.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

          <div className="p-8">
            {status === 'success' ? (
              <div className="flex flex-col items-center gap-4 py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                  <i className="ri-checkbox-circle-fill text-emerald-400 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white">Message Sent!</h3>
                <p className="text-gray-400 text-sm max-w-xs leading-relaxed">
                  Thanks for reaching out! We&apos;ll get back to you soon.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 px-6 py-2.5 border border-white/15 text-gray-300 hover:text-white hover:border-white/30 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-5"
              >
                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Name <span className="text-cyan-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your name"
                      className={inputBase}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Email <span className="text-cyan-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className={inputBase}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Subject
                    <span className="text-gray-600 font-normal ml-1">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    placeholder="e.g. Product question, business inquiry…"
                    className={inputBase}
                  />
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Message <span className="text-cyan-500">*</span>
                    </label>
                    <span className={`text-xs ${charCount > 450 ? 'text-amber-400' : 'text-gray-600'}`}>
                      {charCount} / 500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Write your message here…"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className={`${inputBase} resize-none`}
                  />
                </div>

                {/* Error state */}
                {status === 'error' && (
                  <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/25 text-red-400 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-base"></i>
                    </div>
                    Something went wrong. Please try again.
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-bold rounded-xl text-sm hover:shadow-xl hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
        </div>

        {/* Direct email fallback */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Or email us directly at{' '}
          <a
            href="mailto:info@workplaysave.com"
            className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors duration-200"
          >
            info@workplaysave.com
          </a>
        </p>
      </div>
    </section>
  );
}
